<!-- Forum Detail Modal -->
<template>
  <div
    class="modal fade show forum-detail-modal"
    :class="{ 'd-block': show }"
    v-if="show"
    @click.self="closeModal"
    style="background-color: rgba(0,0,0,0.5);"
  >
    <div class="modal-dialog modal-xl">
      <div class="modal-content">

        <!-- Header -->
        <div class="modal-header">
          <div class="d-flex align-items-center flex-grow-1">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary me-3"
              @click="closeModal"
            >
              <i class="bi bi-arrow-left me-2"></i>Back to Forums
            </button>

            <div class="flex-grow-1">
              <h4 class="modal-title mb-0" v-if="forumLocal">
                <i class="bi bi-chat-square-text me-2"></i>
                {{ forumLocal.subject }}
              </h4>
              <div class="text-muted small" v-if="forumLocal">
                {{ forumLocal.description }}
              </div>
            </div>
          </div>

          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <!-- Body -->
        <div class="modal-body p-0">

          <!-- Forum Info Bar -->
          <div class="forum-info-bar p-3 bg-light border-bottom" v-if="forumLocal">
            <div class="row align-items-center">
              <div class="col-md-6">
                <div class="d-flex align-items-center">
                  <img
                    :src="getAvatarUrl(forumLocal.creator)"
                    alt="Avatar"
                    class="rounded-circle me-2"
                    style="width: 32px; height: 32px;"
                  >
                  <div class="me-3">
                    <small class="text-muted d-block">Created by</small>
                    <div class="fw-semibold">{{ forumLocal.creator?.name }}</div>
                  </div>

                  <div class="vr mx-3"></div>

                  <div>
                    <small class="text-muted d-block">Working Group</small>
                    <span class="badge bg-secondary">{{ forumLocal.working_group?.name }}</span>
                  </div>
                </div>
              </div>

              <div class="col-md-6 text-end">
                <div class="d-flex align-items-center justify-content-end gap-3">
                  <div>
                    <small class="text-muted d-block">Topics</small>
                    <div class="fw-bold">{{ topics.length }}</div>
                  </div>

                  <!-- ALWAYS allow creating topics -->
                  <button class="btn btn-primary btn-sm" @click="showNewTopicModal = true">
                    <i class="bi bi-plus me-2"></i>New Topic
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Topics Section -->
          <div class="topics-section">

            <!-- Header -->
            <div class="p-3 border-bottom">
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                  <i class="bi bi-list-ul me-2"></i>Topics
                </h6>

                <div class="d-flex gap-2">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Search topics..."
                    v-model="searchQuery"
                    @keyup.enter="searchTopics"
                    style="width: 200px;"
                  >
                  <button class="btn btn-outline-secondary btn-sm" @click="searchTopics">
                    <i class="bi bi-search"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Content -->
            <div class="topics-content" style="max-height: 500px; overflow-y: auto;">

              <div v-if="loading" class="text-center py-4">
                <div class="spinner-border text-primary"></div>
              </div>

              <div v-else-if="error" class="alert alert-danger m-3">
                {{ error }}
                <button class="btn btn-sm btn-outline-danger ms-2" @click="loadTopics">Retry</button>
              </div>

              <!-- Topic List -->
              <div v-else-if="topics.length" class="topics-list">
                <div
                  v-for="topic in topics"
                  :key="topic.id"
                  class="topic-item p-3 border-bottom topic-hover"
                  @click="viewTopic(topic)"
                  style="cursor:pointer"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <h6 class="text-primary mb-1">{{ topic.title }}</h6>
                      <p class="text-muted small">{{ truncateText(topic.content,150) }}</p>
                      <div class="text-muted small d-flex align-items-center">
                        <img
                          :src="getAvatarUrl(topic.creator)"
                          class="rounded-circle me-2"
                          style="width:24px;height:24px"
                        >
                        <span class="me-3">{{ topic.creator?.name }}</span>
                        <i class="bi bi-clock me-1"></i>{{ formatDate(topic.created_at) }}
                      </div>
                    </div>

                    <i class="bi bi-chevron-right text-muted"></i>
                  </div>
                </div>
              </div>

              <!-- Empty -->
              <div v-else class="text-center py-5">
                <i class="bi bi-chat-square-text text-muted" style="font-size: var(--font-size-4xl)"></i>
                <h5 class="mt-3 text-muted">No topics found</h5>
                <p class="text-muted">Be the first to start the discussion</p>

                <button class="btn btn-primary" @click="showNewTopicModal = true">
                  <i class="bi bi-plus me-2"></i>Create Topic
                </button>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- New Topic Modal -->
    <div
      class="modal fade"
      :class="{ show: showNewTopicModal, 'd-block': showNewTopicModal }"
      v-if="showNewTopicModal"
      @click.self="closeTopicModal"
      style="background-color:rgba(0,0,0,0.6);z-index:1060"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">

          <div class="modal-header">
            <h5 class="modal-title">New Topic</h5>
            <button type="button" class="btn-close" @click="closeTopicModal"></button>
          </div>

          <div class="modal-body">
            <form @submit.prevent="saveTopic">

              <div class="mb-3">
                <label class="form-label">Title *</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="topicForm.title"
                  required
                  maxlength="255"
                >
              </div>

              <div class="mb-3">
                <label class="form-label">Content *</label>
                <textarea
                  class="form-control"
                  rows="6"
                  v-model="topicForm.content"
                  required
                ></textarea>
              </div>

            </form>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeTopicModal">Cancel</button>
            <button class="btn btn-primary" @click="saveTopic" :disabled="saving || !isTopicFormValid">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              Create Topic
            </button>
          </div>

        </div>
      </div>
    </div>

    <!-- Topic Detail Modal -->
    <TopicDetailModal
      :show="showTopicModal"
      :topic="selectedTopic"
      :forum="forumLocal"
      :can-comment="true"
    @close="closeTopicModal"
    @topic-updated="onTopicUpdated"
    />
  </div>
</template>

<script>
import ForumService from '@/services/forums/ForumServices'
import TopicDetailModal from './TopicDetailModal.vue'
import UserService from '@/services/users/UserService'
import { avatarMixin } from '@/mixins/avatarMixin'

export default {
  name: 'ForumDetailModal',
  mixins: [avatarMixin],
  components: { TopicDetailModal },

  props: {
    show: Boolean,
    forumId: [String, Number],
    forum: Object
  },

  data() {
    return {
      forumLocal: null,
      topics: [],
      loading: false,
      saving: false,
      error: null,
      searchQuery: "",
      showNewTopicModal: false,
      showTopicModal: false,
      selectedTopic: null,

      topicForm: {
        title: "",
        content: "",
      },

      currentUserId: null,
      currentUserRoles: []
    }
  },

  computed: {
    isTopicFormValid() {
      return this.topicForm.title.length >= 3 && this.topicForm.content.length >= 5;
    },

    normalizedForum() {
      if (!this.forum) return null;

      // EDCTP admin wrapped response
      if (this.forum.working_group) {
        if (this.forum.forum) {
          return this.forum.forum; // real forum with id
        }

        // Fallback: inject id
        return {
          id: this.forumId,   // 👈 THIS IS THE FIX
          subject: this.forum.working_group.name + " Forum",
          description: "Discussion space for " + this.forum.working_group.name,
          working_group: this.forum.working_group,
          creator: this.forum.working_group.admin ?? null,
        };
      }

      return this.forum;
    }
  },

  watch: {
    forum: {
      immediate: true,
      deep: true,
      handler() {
        this.forumLocal = this.normalizedForum;
      }
    },

    show(newVal) {
      if (newVal) {
        this.loadTopics();
        this.getCurrentUser();
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    }
  },

  methods: {
    async loadTopics() {
      if (!this.forumLocal?.id) {
        this.topics = [];
        return;
      }

      this.loading = true;
      try {
        const response = await ForumService.getForumTopics(this.forumLocal.id);
        this.topics = response?.data?.data?.data || [];
      } catch {
        this.error = "Failed to load topics";
      } finally {
        this.loading = false;
      }
    },

    async getCurrentUser() {
      const response = await UserService.getCurrentUser();
      const user = response?.Data?.data?.user;
      if (user) {
        this.currentUserId = user.id;
        this.currentUserRoles = user.roles?.map(r => r.name) || [];
      }
    },

    searchTopics() {
      this.loadTopics();
    },

    viewTopic(topic) {
      this.selectedTopic = topic;
      this.showTopicModal = true;
    },

    async saveTopic() {
      if (!this.isTopicFormValid) return;

      this.saving = true;
      try {
        const res = await ForumService.createTopic(this.forumLocal.id, this.topicForm);

        if (res?.success) {
          this.closeTopicModal();
          this.loadTopics();
        }
      } finally {
        this.saving = false;
      }
    },

    closeModal() {
      this.$emit("close");
    },

    closeTopicModal() {
      this.showNewTopicModal = false;
      this.showTopicModal = false;
      this.topicForm = { title: "", content: "" };
    },

    onTopicUpdated() {
      this.loadTopics();
    },

    truncateText(t, len) {
      if (!t) return "";
      return t.length > len ? t.substring(0, len) + "..." : t;
    },

    getAvatarUrl(user) {
      return this.getUserAvatar(user);
    },

    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
    }
  }
}
</script>

<style scoped>
.topic-hover:hover {
  background: #f8f9fa;
}
</style>
